import { Camera } from './Camera';
import { Vector3, Quaternion } from '../types';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { InputEventManager } from '../core/InputEventManager';
import { AppSettingsData } from '../core/AppSettingsData';
import * as THREE from 'three';

export class FlyCamera extends Camera {
    public readonly moveSpeed: InterpolatedProperty<number>;
    public readonly boostMultiplier: InterpolatedProperty<number>;
    public readonly mouseSensitivity: InterpolatedProperty<number>;
    public readonly smoothing: InterpolatedProperty<number>;

    private _velocity: Vector3 = Vector3.zero();
    private _pitch: number = 0;
    private _yaw: number = 0;

    constructor(name: string = 'FlyCamera', appSettings: AppSettingsData) {
        // Use the default 'simple' transform from the Camera base class.
        // All movement/smoothing is handled manually in updateControls.
        super(name, 'fixed_offset', appSettings);

        const group = 'FlyCamera Controls';
        this.moveSpeed = new InterpolatedProperty('Move Speed', 5, 5, 0.2, group);
        this.boostMultiplier = new InterpolatedProperty('Boost Multiplier', 3, 3, 0.2, group);
        this.mouseSensitivity = new InterpolatedProperty('Mouse Sensitivity', 0.1, 0.1, 0.2, group);
        // This is a damping factor, 1.0 = no damping, 0.0 = immediate stop.
        this.smoothing = new InterpolatedProperty('Smoothing', 0.85, 0.85, 0.2, group); 
    }

    public override updateControls(deltaTime: number): void {
        const input = InputEventManager.Instance;
        let inputReceived = false;

        // --- Mouse Look ---
        if (input.isMouseDown) {
            inputReceived = true;
            this._yaw -= input.mouseDelta.x * (this.mouseSensitivity.value ?? 0.1) * deltaTime;
            this._pitch -= input.mouseDelta.y * (this.mouseSensitivity.value ?? 0.1) * deltaTime;

            const pi_half = Math.PI / 2;
            this._pitch = Math.max(-pi_half + 0.01, Math.min(pi_half - 0.01, this._pitch));

            this.updateRotationFromAngles();
        }

        // --- Keyboard Movement ---
        const pressedKeys = input.pressedKeys;
        let moveDirection = Vector3.zero();

        if (pressedKeys.has('w')) {
            inputReceived = true;
            moveDirection = moveDirection.add(this.transform.getForwardVector());
        }
        if (pressedKeys.has('s')) {
            inputReceived = true;
            moveDirection = moveDirection.subtract(this.transform.getForwardVector());
        }
        if (pressedKeys.has('d')) {
            inputReceived = true;
            moveDirection = moveDirection.add(this.transform.getRightVector());
        }
        if (pressedKeys.has('a')) {
            inputReceived = true;
            moveDirection = moveDirection.subtract(this.transform.getRightVector());
        }
        if (pressedKeys.has('e')) {
            inputReceived = true;
            moveDirection = moveDirection.add(Vector3.up());
        }
        if (pressedKeys.has('q')) {
            inputReceived = true;
            moveDirection = moveDirection.subtract(Vector3.up());
        }
        
        // Debug log when input is received
        if (inputReceived) {
            //console.log(`🚁 FlyCamera input: keys=${Array.from(pressedKeys).join(',')}, mouse=${input.isMouseDown}, moveDir=${moveDirection.toString()}`);
        }
        
        // --- Boosting ---
        const isBoosting = pressedKeys.has('shift');
        const currentSpeed = (this.moveSpeed.value ?? 5) * (isBoosting ? (this.boostMultiplier.value ?? 3) : 1);

        // --- Apply Acceleration ---
        if (moveDirection.lengthSquared() > 0) {
            const scaledMove = moveDirection.normalize().multiply(currentSpeed);
            this._velocity = this._velocity.add(scaledMove);
        }
        
        // --- Apply Velocity & Damping ---
        const currentPosition = this.transform.position.value;
        if (currentPosition) {
            const frameVelocity = this._velocity.multiply(deltaTime);
            this.transform.position.value = currentPosition.add(frameVelocity);
        }
        
        // Apply damping (smoothing)
        this._velocity = this._velocity.multiply(this.smoothing.value ?? 0.85);

        // Stop movement if velocity is negligible
        if (this._velocity.lengthSquared() < 0.0001) {
            this._velocity.set(0, 0, 0);
        }
    }

    private updateRotationFromAngles(): void {
        const pitchQuat = Quaternion.fromAxisAngle(new Vector3(1, 0, 0), this._pitch);
        const yawQuat = Quaternion.fromAxisAngle(new Vector3(0, 1, 0), this._yaw);
        
        const finalRotation = yawQuat.multiply(pitchQuat).normalize();
        
        // Directly set the rotation on the simple transform property
        this.transform.rotation.value = finalRotation;
    }

    /**
     * Initialize pitch and yaw from a quaternion rotation
     * This is needed when copying rotation from another camera
     */
    public setRotationFromQuaternion(quaternion: Quaternion): void {
        // Convert quaternion to Euler angles
        const threeQuat = quaternion.toThree();
        const euler = new THREE.Euler().setFromQuaternion(threeQuat, 'YXZ');
        
        this._yaw = euler.y;
        this._pitch = euler.x;
        
        // Clamp pitch to valid range
        const pi_half = Math.PI / 2;
        this._pitch = Math.max(-pi_half + 0.01, Math.min(pi_half - 0.01, this._pitch));
        
        // Update the transform rotation
        this.updateRotationFromAngles();
    }
} 